import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  CurrencyInr, DownloadSimple, Plus, Wallet, CaretDown, CaretUp, Trash,
  ArrowSquareOut,
} from "@phosphor-icons/react";

const STATUS_CLS = {
  Paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Partial: "bg-amber-50 text-amber-700 border-amber-200",
  Unpaid: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function Ledger() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState({ rows: [] });
  const [expanded, setExpanded] = useState(null);

  const [adj, setAdj] = useState({ employee_id: "", month, kind: "bonus", amount: "", reason: "", currency: "INR" });
  const [pay, setPay] = useState({ employee_id: "", month, amount: "", method: "Bank Transfer", reference: "", payment_date: new Date().toISOString().slice(0, 10) });
  const [showAdj, setShowAdj] = useState(false);
  const [showPay, setShowPay] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/ledger?month=${month}`);
      setData(data);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  }, [month]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setAdj((a) => ({ ...a, month })); setPay((p) => ({ ...p, month })); }, [month]);

  const download = async (format) => {
    try {
      const res = await api.get(`/ledger/export?month=${month}&format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      const ext = format === "excel" ? "xlsx" : format;
      a.download = `payout_ledger_${month}.${ext}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const addAdjust = async (e) => {
    e.preventDefault();
    try {
      await api.post("/ledger/adjust", { ...adj, amount: Number(adj.amount) });
      toast.success("Adjustment recorded");
      setAdj({ ...adj, amount: "", reason: "" }); setShowAdj(false); load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const recordPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post("/ledger/pay", { ...pay, amount: Number(pay.amount) });
      toast.success("Payment recorded");
      setPay({ ...pay, amount: "", reference: "" }); setShowPay(false); load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const deleteAdj = async (aid) => {
    if (!window.confirm("Remove this adjustment?")) return;
    try { await api.delete(`/ledger/adjust/${aid}`); load(); }
    catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };
  const deletePay = async (pid) => {
    if (!window.confirm("Remove this payment?")) return;
    try { await api.delete(`/ledger/pay/${pid}`); load(); }
    catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const employeeOptions = data.rows.map((r) => ({ id: r.employee_id, name: r.employee_name }));

  const totals = data.rows.reduce((s, r) => ({
    approved: s.approved + r.approved_amount, bonus: s.bonus + r.bonus,
    penalty: s.penalty + r.penalty, due: s.due + r.total_due,
    paid: s.paid + r.paid, pending: s.pending + r.pending_amount,
  }), { approved: 0, bonus: 0, penalty: 0, due: 0, paid: 0, pending: 0 });

  return (
    <div className="space-y-5" data-testid="ledger-page">
      <div>
        <h2 className="text-2xl font-semibold flex items-center gap-2"><Wallet size={22} /> Payout Ledger</h2>
        <p className="text-sm text-slate-500">Employee-wise monthly summary of approved incentives, bonuses, penalties and payments. HR + Admin.</p>
      </div>

      <div className="flex flex-wrap gap-2 bg-white border border-slate-200 rounded-md p-3 items-center">
        <div>
          <label className="text-xs text-slate-500">Month</label>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                 data-testid="ledger-month"
                 className="block border border-slate-200 rounded-md px-3 py-2 text-sm" />
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <button onClick={() => setShowAdj(true)} data-testid="add-adjustment"
                  className="text-sm border border-slate-200 hover:bg-slate-50 rounded-md px-3 py-2 inline-flex items-center gap-1">
            <Plus size={14} /> Bonus / Penalty
          </button>
          <button onClick={() => setShowPay(true)} data-testid="record-payment"
                  className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-3 py-2 inline-flex items-center gap-1">
            <CurrencyInr size={14} /> Record payment
          </button>
          <button onClick={() => download("excel")} data-testid="dl-ledger-excel" className="text-sm border border-slate-200 hover:bg-slate-50 rounded-md px-3 py-2 inline-flex items-center gap-1">
            <DownloadSimple size={14} /> Excel
          </button>
          <button onClick={() => download("pdf")} data-testid="dl-ledger-pdf" className="text-sm border border-slate-200 hover:bg-slate-50 rounded-md px-3 py-2 inline-flex items-center gap-1">
            <DownloadSimple size={14} /> PDF
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="ledger-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-widest">
            <tr>
              <th className="text-left px-3 py-2">Employee</th>
              <th className="text-left px-3 py-2">Team</th>
              <th className="text-right px-3 py-2">Approved</th>
              <th className="text-right px-3 py-2">Bonus</th>
              <th className="text-right px-3 py-2">Penalty</th>
              <th className="text-right px-3 py-2">Total Due</th>
              <th className="text-right px-3 py-2">Paid</th>
              <th className="text-right px-3 py-2">Pending</th>
              <th className="text-left px-3 py-2">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan={10} className="text-center text-slate-400 py-8">No approved incentives, bonuses or payments for {month}.</td></tr>}
            {data.rows.map((r) => (
              <>
                <tr key={r.employee_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`ledger-row-${r.employee_id}`}>
                  <td className="px-3 py-2 font-medium">{r.employee_name}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{r.team || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.approved_amount.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{r.bonus.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-red-700">{r.penalty.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{r.total_due.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.paid.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">{r.pending_amount.toFixed(2)}</td>
                  <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_CLS[r.payment_status] || STATUS_CLS.Unpaid}`}>{r.payment_status}</span></td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setExpanded(expanded === r.employee_id ? null : r.employee_id)} data-testid={`expand-${r.employee_id}`}
                            className="text-slate-500 hover:text-sky-700">
                      {expanded === r.employee_id ? <CaretUp size={14} /> : <CaretDown size={14} />}
                    </button>
                  </td>
                </tr>
                {expanded === r.employee_id && (
                  <tr className="bg-slate-50">
                    <td colSpan={10} className="px-4 py-3">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                        <div>
                          <div className="font-semibold text-slate-600 uppercase tracking-widest text-[10px] mb-2">Approved Incentives</div>
                          {(r.records || []).length === 0 && <span className="text-slate-400">none</span>}
                          {(r.records || []).map((rec) => (
                            <div key={rec.id} className="flex justify-between py-0.5">
                              <span>{rec.type.replace(/_/g, " ")} — {rec.source_ref?.company || "—"}</span>
                              <span className="tabular-nums">{rec.currency} {(rec.amount || 0).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-600 uppercase tracking-widest text-[10px] mb-2">Adjustments</div>
                          {(r.adjustments || []).length === 0 && <span className="text-slate-400">none</span>}
                          {(r.adjustments || []).map((a) => (
                            <div key={a.id} className="flex justify-between items-center py-0.5">
                              <span className={a.kind === "bonus" ? "text-emerald-700" : "text-red-700"}>
                                {a.kind === "bonus" ? "+" : "−"} {a.reason}
                                {a.rule_key && <span className="text-slate-400 text-[10px] ml-1">[{a.rule_key}]</span>}
                              </span>
                              <span className="flex items-center gap-1">
                                <span className="tabular-nums">{a.currency} {a.amount.toFixed(2)}</span>
                                <button onClick={() => deleteAdj(a.id)} className="text-slate-400 hover:text-red-600"><Trash size={10} /></button>
                              </span>
                            </div>
                          ))}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-600 uppercase tracking-widest text-[10px] mb-2">Payments</div>
                          {(r.payments || []).length === 0 && <span className="text-slate-400">none</span>}
                          {(r.payments || []).map((p) => (
                            <div key={p.id} className="flex justify-between items-center py-0.5">
                              <span>{p.payment_date} · {p.method} · by {p.paid_by_name}
                                {p.reference && <> · <span className="text-slate-500">{p.reference}</span></>}
                              </span>
                              <span className="flex items-center gap-1">
                                <span className="tabular-nums">{p.amount.toFixed(2)}</span>
                                <button onClick={() => deletePay(p.id)} className="text-slate-400 hover:text-red-600"><Trash size={10} /></button>
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
            {data.rows.length > 0 && (
              <tr className="border-t border-slate-200 bg-slate-50 font-semibold">
                <td colSpan={2} className="px-3 py-2 text-right">Totals</td>
                <td className="px-3 py-2 text-right tabular-nums">{totals.approved.toFixed(2)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{totals.bonus.toFixed(2)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-red-700">{totals.penalty.toFixed(2)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{totals.due.toFixed(2)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{totals.paid.toFixed(2)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{totals.pending.toFixed(2)}</td>
                <td colSpan={2}></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showAdj && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm grid place-items-center p-4 z-50">
          <form onSubmit={addAdjust} className="bg-white border border-slate-200 rounded-xl p-6 w-full max-w-md" data-testid="adjust-modal">
            <h3 className="font-semibold mb-4">Add Bonus / Penalty</h3>
            <div className="space-y-3">
              <div><label className="text-xs text-slate-500">Employee</label>
                <select required value={adj.employee_id} onChange={(e) => setAdj((a) => ({ ...a, employee_id: e.target.value }))}
                        data-testid="adj-employee"
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                  <option value="">— select —</option>
                  {employeeOptions.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div><label className="text-xs text-slate-500">Kind</label>
                <select value={adj.kind} onChange={(e) => setAdj((a) => ({ ...a, kind: e.target.value }))}
                        data-testid="adj-kind"
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                  <option value="bonus">Bonus (add to due)</option>
                  <option value="penalty">Penalty (deduct from due)</option>
                </select>
              </div>
              <div><label className="text-xs text-slate-500">Amount</label>
                <input required type="number" step="0.01" value={adj.amount} onChange={(e) => setAdj((a) => ({ ...a, amount: e.target.value }))}
                       data-testid="adj-amount"
                       className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              </div>
              <div><label className="text-xs text-slate-500">Reason</label>
                <input required value={adj.reason} onChange={(e) => setAdj((a) => ({ ...a, reason: e.target.value }))}
                       data-testid="adj-reason"
                       className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setShowAdj(false)} className="px-4 py-2 text-sm rounded-md border border-slate-200">Cancel</button>
              <button type="submit" data-testid="adj-submit" className="px-4 py-2 text-sm rounded-md bg-sky-600 hover:bg-sky-700 text-white">Save</button>
            </div>
          </form>
        </div>
      )}

      {showPay && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm grid place-items-center p-4 z-50">
          <form onSubmit={recordPayment} className="bg-white border border-slate-200 rounded-xl p-6 w-full max-w-md" data-testid="pay-modal">
            <h3 className="font-semibold mb-4">Record Payment</h3>
            <div className="space-y-3">
              <div><label className="text-xs text-slate-500">Employee</label>
                <select required value={pay.employee_id} onChange={(e) => setPay((p) => ({ ...p, employee_id: e.target.value }))}
                        data-testid="pay-employee"
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                  <option value="">— select —</option>
                  {employeeOptions.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div><label className="text-xs text-slate-500">Amount</label>
                <input required type="number" step="0.01" value={pay.amount} onChange={(e) => setPay((p) => ({ ...p, amount: e.target.value }))}
                       data-testid="pay-amount"
                       className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              </div>
              <div><label className="text-xs text-slate-500">Method</label>
                <select value={pay.method} onChange={(e) => setPay((p) => ({ ...p, method: e.target.value }))}
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                  <option>Bank Transfer</option><option>Cheque</option><option>Cash</option><option>UPI</option><option>Other</option>
                </select>
              </div>
              <div><label className="text-xs text-slate-500">Reference (txn #)</label>
                <input value={pay.reference} onChange={(e) => setPay((p) => ({ ...p, reference: e.target.value }))}
                       className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              </div>
              <div><label className="text-xs text-slate-500">Payment date</label>
                <input type="date" value={pay.payment_date} onChange={(e) => setPay((p) => ({ ...p, payment_date: e.target.value }))}
                       className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setShowPay(false)} className="px-4 py-2 text-sm rounded-md border border-slate-200">Cancel</button>
              <button type="submit" data-testid="pay-submit" className="px-4 py-2 text-sm rounded-md bg-emerald-600 hover:bg-emerald-700 text-white">Record</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
